import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Link from 'flarum/common/components/Link';
import { QUEST_RARITIES, QUEST_TYPE_COLORS, QUEST_TYPE_ICONS } from '../../common/quests';
import { activeQuests, questProgressFor, questTitle, questTokens } from '../quests';
import { transPlain, getContrastColor } from '../../common';
const FILTERS = ['regular', 'temporary', 'completed'];

/**
 * "Задания" modal, opened from the Arena tag page (see addArenaTab) —
 * analogous to BattlePassModal, but its own two-column card grid (rather
 * than BattlePassBar's slider/track), filterable by a "Обычные / Временные /
 * Выполненные" dropdown (see FILTERS/categoryOf()) crossed with a difficulty
 * dropdown (see RarityFilter/QUEST_RARITIES) — two selects, each in its own
 * plaque (see renderFilters()). Guests and logged-out viewers see the full
 * unfiltered list with no progress (the quests still double as a "what you
 * can earn" showcase, same reasoning as the Battle Pass modal being visible
 * to guests) — the filter panel only appears once there's a per-user
 * completion state to actually split by.
 */
export default class QuestsModal extends Modal {
  // Which of the "Обычные / Временные / Выполненные" categories is showing.
  // Guests never see the filters (see content()) and always get the full,
  // unfiltered list — this only matters once a logged-in user picks one.
  filter = 'regular';

  // Independent of `filter` above — narrows within whichever category is
  // selected down to one difficulty, or 'any' (the default) for no extra
  // narrowing.
  rarityFilter = 'any';
  className() {
    return 'ArenaQuestsModal Modal--large';
  }
  title() {
    return m('[', null, m("i", {
      className: "fas fa-tasks"
    }), ' ', app.translator.trans('forumaker-arena.forum.quests.title'));
  }
  content() {
    const quests = activeQuests();
    const progress = questProgressFor(app.session.user);
    const tokens = questTokens(app.session.user);

    // Optional neutral banner set in the admin panel (Задания → Фон) —
    // painted as the modal body's own background-image so it sits behind
    // the intro text, token balance, and quest list alike. Same
    // upload-or-URL setting pattern and rendering approach as
    // BattlePassBar's arenaBattlePassBackgroundUrl handling.
    const backgroundUrl = app.forum.attribute('arenaQuestsBackgroundUrl');
    const style = backgroundUrl ? {
      backgroundImage: `url("${backgroundUrl}")`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    } : undefined;
    return m("div", {
      className: "Modal-body ArenaQuestsModal-body",
      style: style
    }, m("p", {
      className: "ArenaQuestsModal-intro"
    }, app.translator.trans('forumaker-arena.forum.quests.modal_intro', {
      shopLink: m(Link, {
        href: "/t/shop"
      }, transPlain('forumaker-arena.forum.quests.shop_link_text'))
    }), app.session.user && m('[', null, ' ', app.translator.trans('forumaker-arena.forum.quests.tokens_balance', {
      // `count` (a plain number) drives the plural word choice;
      // `tokens` is the bolded VNode actually shown — ICU's `#`
      // plural shorthand can't be individually styled, so the
      // locale string uses two separate placeholders instead
      // (see tokens_balance's own comment).
      count: tokens,
      tokens: m("strong", null, tokens)
    }))), app.session.user && quests.length > 0 && this.renderFilters(), quests.length === 0 ? m("p", {
      className: "helpText ArenaQuestsModal-empty"
    }, app.translator.trans('forumaker-arena.forum.quests.empty')) : (() => {
      // Guests get the full, unfiltered list (still a "what you can
      // earn" showcase, same reasoning as the tokens balance being
      // hidden for them) — only a logged-in user's own progress makes
      // the regular/temporary/completed split meaningful.
      const visible = app.session.user ? quests.filter(quest => this.categoryOf(quest, progress[quest.id]) === this.filter && (this.rarityFilter === 'any' || quest.rarity === this.rarityFilter)) : quests;
      return visible.length === 0 ? m("p", {
        className: "helpText ArenaQuestsModal-empty"
      }, app.translator.trans('forumaker-arena.forum.quests.empty_filtered')) : m("div", {
        className: "ArenaQuestsModal-list"
      }, visible.map(quest => this.renderQuestRow(quest, progress[quest.id])));
    })());
  }
  renderFilters() {
    return m("div", {
      className: "ArenaQuestsModal-filtersPanel"
    }, m("div", {
      className: "ArenaQuestsModal-filterPlaque"
    }, m("select", {
      className: "FormControl ArenaQuestsModal-filterSelect",
      value: this.filter,
      onchange: e => {
        this.filter = e.target.value;
      }
    }, FILTERS.map(key => m("option", {
      value: key
    }, app.translator.trans(`forumaker-arena.forum.quests.filter_${key}`)))), m("i", {
      className: "fas fa-chevron-down ArenaQuestsModal-filterSelectChevron"
    })), m("div", {
      className: "ArenaQuestsModal-filterPlaque"
    }, m("select", {
      className: "FormControl ArenaQuestsModal-filterSelect",
      value: this.rarityFilter,
      onchange: e => {
        this.rarityFilter = e.target.value;
      }
    }, m("option", {
      value: "any"
    }, transPlain('forumaker-arena.forum.quests.rarity_any')), QUEST_RARITIES.map(r =>
    // Plural wording here ("Лёгкие", not "Лёгкое") — this option
    // describes a whole filtered set of quests, unlike the same
    // rarity_* key's singular use on one quest's own badge below.
    m("option", {
      value: r
    }, transPlain(`forumaker-arena.forum.quests.filter_rarity_${r}`)))), m("i", {
      className: "fas fa-chevron-down ArenaQuestsModal-filterSelectChevron"
    })));
  }

  /**
   * Which of the three filter categories a quest belongs to. Completion always
   * wins over having a deadline — once done, a quest is "Выполнено" even if
   * it also had a time limit; "Временное" only describes quests still in
   * progress against a clock.
   */
  categoryOf(quest, progress) {
    if (progress?.completed_at) return 'completed';
    return quest.deadline ? 'temporary' : 'regular';
  }

  /**
   * "Осталось 3 дня" for a quest with a deadline.
   *
   * activeQuests() has already dropped anything past its deadline, so this
   * only ever formats a future date — but it still guards, since the modal
   * can stay open across the moment one expires.
   *
   * Deliberately coarse: days, then hours, then minutes. The exact timestamp
   * is the admin's business; a player only needs to know whether there's
   * still time to play for it.
   */
  deadlineLabel(quest) {
    if (!quest.deadline) return null;
    const ms = new Date(quest.deadline).getTime() - Date.now();
    if (!Number.isFinite(ms) || ms <= 0) return null;
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return transPlain('forumaker-arena.forum.quests.deadline_days', {
      count: days
    });
    if (hours > 0) return transPlain('forumaker-arena.forum.quests.deadline_hours', {
      count: hours
    });
    return transPlain('forumaker-arena.forum.quests.deadline_minutes', {
      count: Math.max(1, minutes)
    });
  }
  renderQuestRow(quest, progress) {
    const current = progress?.progress ?? 0;
    const completed = !!progress?.completed_at;
    const pct = Math.max(0, Math.min(100, Math.round(current / Math.max(1, quest.target) * 100)));
    // Hidden once the quest is done — the countdown stops mattering then.
    const deadline = completed ? null : this.deadlineLabel(quest);
    // Per-quest admin override (see ArenaAdminPage's quest editor) beats the
    // type-keyed default — same priority order as the icon below. Fallback
    // stays within the approved palette (not var(--primary)) since
    // getContrastColor() needs a real hex to read RGB out of.
    const accent = quest.color || QUEST_TYPE_COLORS[quest.type] || '#3F6F8D';
    const accentText = getContrastColor(accent);
    const icon = quest.icon || QUEST_TYPE_ICONS[quest.type] || 'fas fa-tasks';

    // A manually-uploaded picture (set in the admin editor, see
    // UploadQuestImageController) takes priority over play_card's own
    // linked-card art, which in turn beats the generic type icon.
    const image = quest.config.image_url || (quest.type === 'play_card' ? quest.config.cardImageUrl : undefined);
    return (
      // Flat neutral plaque now — same rgba tint as -filtersPanel/
      // -filterPlaque, no top cap anymore. The accent is spent only on the
      // icon and the footer strip below (see -rowIcon/-rowBadge).
      m("div", {
        className: `ArenaQuestsModal-row ${completed ? 'is-completed' : ''}`
      }, m("div", {
        className: "ArenaQuestsModal-rowMain"
      }, image ? m("img", {
        className: "ArenaQuestsModal-rowThumb",
        src: image,
        alt: ""
      }) : m("i", {
        className: `ArenaQuestsModal-rowIcon ${icon}`,
        style: {
          color: accent
        }
      }), m("div", {
        className: "ArenaQuestsModal-rowBody"
      }, m("div", {
        className: "ArenaQuestsModal-rowTitle"
      }, questTitle(quest)), quest.description && m("div", {
        className: "ArenaQuestsModal-rowDescription"
      }, quest.description), app.session.user && !completed && m("div", {
        className: "ArenaQuestsModal-rowProgress"
      }, m("div", {
        className: "ArenaQuestsModal-rowProgressBar"
      }, m("div", {
        className: "ArenaQuestsModal-rowProgressFill",
        style: {
          width: `${pct}%`
        }
      })), m("span", {
        className: "ArenaQuestsModal-rowProgressText"
      }, current, " / ", quest.target)))), m("div", {
        className: "ArenaQuestsModal-rowBadge",
        style: {
          background: accent,
          color: accentText
        }
      }, m("span", {
        className: "ArenaQuestsModal-rowBadgeLabel"
      }, app.translator.trans(`forumaker-arena.forum.quests.rarity_${quest.rarity}`)), (quest.reward_tokens > 0 || !!deadline) && m("span", {
        className: "ArenaQuestsModal-rowBadgeMeta"
      }, quest.reward_tokens > 0 && m("span", {
        className: "ArenaQuestsModal-rowReward"
      }, m("i", {
        className: "fas fa-coins"
      }), " ", quest.reward_tokens), quest.reward_tokens > 0 && !!deadline && m("span", {
        className: "ArenaQuestsModal-rowMetaDivider"
      }), !!deadline && m("span", {
        className: "ArenaQuestsModal-rowDeadline"
      }, m("i", {
        className: "fas fa-hourglass-half"
      }), " ", deadline))))
    );
  }
}
flarum.reg.add('forumaker-arena', 'forum/components/QuestsModal', QuestsModal);